<!-- eslint-disable vue/no-v-html -->
<template>
  <div v-if="tile" class="p-2 w-full sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/5">
    <div
      v-if="tile.title"
      class="flex flex-col p-4 bg-white shadow relative rounded-lg h-full justify-between group"
      :class="{
        'text-white': textWhite,
        'cursor-move': allowSorting
      }"
      :style="{ backgroundColor: tile.backgroundColor, color: tile.textColor }"
    >
      <div class="flex mt-2">
        <div
          v-if="tile.icon && !tile.icon.endsWith('.svg')"
          class="text-sm"
          :class="{
            'bg-white p-2 rounded-lg max-w-10 w-10 max-h-10 h-10 align-middle text-center': tile.iconFramed,
            'rounded-lg overflow-hidden': tile.iconImage
          }"
        >
          <img class="inline-block justify-center" :class="calculateClasses" :src="tile.icon" aria-hidden="true" />
        </div>

        <div v-if="tile.icon && tile.icon.endsWith('svg')" :class="{ 'bg-white p-2 rounded-lg': tile.iconFramed }">
          <inline-svg
            class="fill-current max-w-30 max-h-8"
            aria-hidden="true"
            :class="{ 'w-auto h-6': tile.iconFramed, 'w-auto h-8': !tile.iconFramed }"
            :src="tile.icon"
            @loaded="onSvgLoaded"
          ></inline-svg>
        </div>

        <div
          v-if="$roles.isTeacher && allowSorting"
          ref="classContext"
          class="ml-auto opacity-0 group-hover:opacity-100"
          animate-opacity
        >
          <button type="button" class="button-circle relative z-10" @click="toggleContextMenu(tile.id)">
            <svg viewBox="0 0 16 4" class="fill-current w-4 mt-auto mb-auto" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 2a2 2 0 10-4 0 2 2 0 004 0zm2 0a2 2 0 104 0 2 2 0 00-4 0zm6 0a2 2 0 104 0 2 2 0 00-4 0z"
                fill="#000"
                fill-opacity=".6"
              />
            </svg>
          </button>
          <div
            v-if="showContextMenu"
            v-closable="{
              exclude: ['classContext'],
              handler: 'hideContextMenu'
            }"
            class="absolute right-0 mr-6 w-40 rounded-sm bg-white shadow flex flex-col text-gray-800 z-30"
          >
            <button
              ref="removeTileContext"
              class="hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-left font-semibold p-4"
              @click="startRemoveTile(tile.id)"
            >
              {{ $t("Tile.Actions.RemoveTile") }}
            </button>
            <ConfirmDialog
              v-if="removeTileStartedForId === tile.id"
              :ok-button-text="`${$t('Documents.Functions.Delete')} tile`"
              :ok-button-running-text="`${$t('Documents.Functions.Deleting')} tile`"
              :process-done="false"
              @confirmed="removeTile(tile.id)"
              @cancelled="stopRemoveTile"
            >
              <span slot="header">{{ `${$t("Documents.Functions.Delete")} tile` }}</span>
              <span slot="body" v-html="confirmText(tile)"></span>
            </ConfirmDialog>
          </div>
        </div>
      </div>

      <div v-if="!tile.icon" class="w-10 h-10 rounded-full border-2 border-current opacity-25"></div>

      <router-link
        :is="type"
        :to="to"
        :href="to"
        :target="target"
        class="outline-link absolute block inset-0"
        :aria-label="$t('Common.GoTo') + ' ' + ($te(tile.title) ? $t(tile.title) : tile.title)"
      ></router-link>

      <LocalizeWord
        class="mt-4 sm:mt-12 text-xl font-semibold w-full overflow-ellipsis overflow-hidden title"
        :value="tile.title"
      ></LocalizeWord>
    </div>
  </div>
</template>

<script>
import InlineSvg from "vue-inline-svg";
import LocalizeWord from "@/components/Shared/LocalizeWord";

export default {
  name: "TilesTile",

  data() {
    return {
      showContextMenu: false,
      removeTileStartedForId: null
    };
  },
  props: {
    tile: {
      type: Object,
      required: true
    },
    allowSorting: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    calculateClasses() {
      if (this.tile.icon.indexOf("google.com/s2/favicons") === -1) {
        if (this.tile.iconFramed) {
          return "w-auto h-auto max-w-6 max-h-6";
        }

        if (this.tile.iconImage) {
          return "w-auto h-auto max-w-14 max-h-14";
        }
        return "w-auto h-auto max-w-35 max-h-8";
      }
      return "";
    },

    type() {
      if (this.tile.uri == null) return "a";

      return this.tile.uri.action != "blank" ? "router-link" : "a";
    },

    to() {
      if (this.tile.uri === null) return "";

      var uri = this.tile.uri.uri;

      uri = uri.replace("{routeName}", this.$route.name);

      var params = this.$route.params;
      uri = uri.replace(/\{(.*?)\}/, r0 => {
        return params[r0.substr(1, r0.length - 2)];
      });

      if (this.tile.uri === null) return "";

      switch (this.tile.uri.action) {
        case "tiles":
          return "/tiles/" + uri;
        default:
          return uri;
      }
    },

    target() {
      if (this.tile.uri === null) return "";

      return this.tile.uri.action == "blank" ? "_blank" : "";
    },

    textWhite() {
      var rgb = [255, 255, 255];
      if (this.tile.backgroundColor == null) {
        rgb = [127, 255, 255];
      } else if (this.tile.backgroundColor.startsWith("rgb(")) {
        rgb = this.tile.backgroundColor
          .replace("rgb(", "")
          .replace(")", "")
          .replace(" ", "")
          .split(",")
          .map(p => parseInt("0" + p));
      } else if (this.tile.backgroundColor.startsWith("#")) {
        rgb[0] = parseInt(this.tile.backgroundColor.replace("#", "").substr(0, 2), 16);
        rgb[1] = parseInt(this.tile.backgroundColor.replace("#", "").substr(2, 2), 16);
        rgb[2] = parseInt(this.tile.backgroundColor.replace("#", "").substr(4, 2), 16);
      }

      var yiq = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000;
      return yiq < 128;
    }
  },

  methods: {
    toggleContextMenu() {
      if (this.showContextMenu === true) {
        this.hideContextMenu();
      } else {
        this.showContextMenu = true;
      }
    },

    hideContextMenu() {
      this.showContextMenu = false;
    },

    onSvgLoaded() {
      this.$emit("heightRecalculateRequired");
    }
  },

  components: {
    LocalizeWord,
    InlineSvg,
    ConfirmDialog: () => import("@/components/Shared/ConfirmDialog")
  }
};
</script>

<style scoped>
a:focus-visible + .title {
  @apply underline;
}
</style>
